import type { HolderName } from '@webgame/shared';
import { createInstance, type i18n as I18nextInstance } from 'i18next';
import type { TranslationArgs, TranslationKey } from './keys';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from './locales';
import { en, type Translations } from './resources/en';
import { es } from './resources/es';

const NAMESPACE = 'translation';

/** Fetches a locale's catalog the translator was not given (a browser's other language). */
export type CatalogLoader = (locale: Locale) => Promise<Translations>;

export type LocaleListener = (locale: Locale) => void;

/**
 * Typed facade over i18next. UI code depends on this interface only, so the underlying
 * library can change without touching call sites.
 */
export interface Translator {
  readonly locale: Locale;
  t<K extends TranslationKey>(key: K, ...args: TranslationArgs<K>): string;
  /** Translates a server error code, falling back to a generic message for unknown codes. */
  error(code: string): string;
  /**
   * Translates a key built from data (scene ids, scenario ids...) that the compiler cannot
   * check. Returns null when the key is missing so callers can fall back explicitly.
   */
  lookup(key: string, values?: Record<string, string | number>): string | null;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  setLocale(locale: Locale): Promise<void>;
  /** Notifies after every locale change. Returns an unsubscribe function. */
  subscribe(listener: LocaleListener): () => void;
}

/** A translator holding every catalog (the server, tests, tools). */
export function createTranslator(initialLocale: Locale = DEFAULT_LOCALE): Translator {
  return createTranslatorFrom({ en, es }, initialLocale);
}

/**
 * A translator holding only the catalogs given (NET-CLIENT-CACHE C2: a browser downloads its
 * player's language alone), fetching another one with `load` when the locale changes to it.
 * Catalogs share their keys (a test pins it), so no fallback between languages is needed.
 */
export function createTranslatorFrom(
  catalogs: Partial<Record<Locale, Translations>>,
  initialLocale: Locale,
  load?: CatalogLoader,
): Translator {
  const instance: I18nextInstance = createInstance();
  const resources: Partial<Record<Locale, Record<string, Translations>>> = {};
  for (const [id, catalog] of Object.entries(catalogs) as [Locale, Translations][]) {
    resources[id] = { [NAMESPACE]: catalog };
  }
  void instance.init({
    resources,
    lng: initialLocale,
    fallbackLng: catalogs[DEFAULT_LOCALE] ? DEFAULT_LOCALE : initialLocale,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [NAMESPACE],
    defaultNS: NAMESPACE,
    // Resources are bundled, so initialization completes synchronously.
    initAsync: false,
    // Rendering layers (Preact, Phaser text) never interpret strings as HTML.
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  let locale = initialLocale;
  const listeners = new Set<LocaleListener>();
  const numberFormats = new Map<string, Intl.NumberFormat>();

  // Whether a key's sentence opens with a value (`{{power}} paga…`): a name put in it in lower case
  // (`nameInText`) gets its capital back there.
  const opensWithValue = (key: string): boolean => {
    const template: unknown = instance.getResource(locale, NAMESPACE, key);
    return typeof template === 'string' && template.startsWith('{{');
  };
  const translate = (key: string, values?: Record<string, string | number>): string =>
    written(locale, instance.t(key, values ?? {}), opensWithValue(key));

  return {
    get locale() {
      return locale;
    },
    t(key, ...args) {
      return translate(key, args[0]);
    },
    error(code) {
      const key = `errors.${code}`;
      return instance.exists(key) ? translate(key) : translate('errors.unknown');
    },
    lookup(key, values) {
      if (!instance.exists(key)) return null;
      // Namespaces exist too; only leaf strings are translations.
      const value: unknown = instance.t(key, { ...values, returnObjects: true });
      return typeof value === 'string' ? written(locale, value, opensWithValue(key)) : null;
    },
    formatNumber(value, options) {
      const cacheKey = `${locale}:${JSON.stringify(options ?? {})}`;
      let format = numberFormats.get(cacheKey);
      if (!format) {
        format = new Intl.NumberFormat(locale, options);
        numberFormats.set(cacheKey, format);
      }
      return format.format(value);
    },
    async setLocale(next) {
      if (next === locale) return;
      if (!instance.hasResourceBundle(next, NAMESPACE)) {
        if (!load) throw new Error(`No catalog for ${next}`);
        instance.addResourceBundle(next, NAMESPACE, await load(next));
      }
      await instance.changeLanguage(next);
      locale = next;
      for (const listener of listeners) listener(next);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * The articles a name may open with as a title (`El pabellón de cazadores`, `The hunters' lodge`):
 * inside a sentence they are written in lower case (`nameInText`).
 */
const TITLE_ARTICLES: Readonly<Record<Locale, RegExp>> = {
  en: /^The(?= )/u,
  es: /^(El|La|Los|Las)(?= )/u,
};

/**
 * A name as it reads inside a sentence (a place, a zone, a region in a hint, a rumor, a banner):
 * the catalogs name places as titles, their article capitalized, so its article goes to lower
 * case («Lejos, al suroeste del pabellón de cazadores», never «de El pabellón»). Spanish then
 * contracts «de el» and «a el» as it writes the sentence (`written`). Never for a name that opens
 * a sentence or stands alone (a banner's title, a map's label).
 */
export function nameInText(translator: Pick<Translator, 'locale'>, name: string): string {
  return name.replace(TITLE_ARTICLES[translator.locale], (article) => article.toLowerCase());
}

/** The articles `nameInText` writes in lower case, as a sentence may open with them. */
const TEXT_ARTICLES: Readonly<Record<Locale, RegExp>> = {
  en: /^the(?= )/u,
  es: /^(el|la|los|las)(?= )/u,
};

/**
 * A sentence as its language writes it once its values are in: Spanish contracts the article «el»
 * after «de» and «a» (`del`, `al`), which a value written in lower case (`nameInText`) leaves
 * apart in the template («al suroeste de {{place}}»). A capitalized article (a proper name's own,
 * «El Salvador») is never contracted. A sentence that opens with a value (`opens`) opens with a
 * capital: a name written in lower case there («los Desposeídos») gets its article's back, so a
 * name is put in lower case wherever it goes and never asks which template it lands in.
 */
function written(locale: Locale, text: string, opens: boolean): string {
  const capital = opens
    ? text.replace(
        TEXT_ARTICLES[locale],
        (article) => article.charAt(0).toUpperCase() + article.slice(1),
      )
    : text;
  if (locale !== 'es' || !capital.includes(' el')) return capital;
  return capital.replace(/(?<!\p{L})(de|a|De|A) el(?!\p{L})/gu, (_, prep: string) => `${prep}l`);
}

/**
 * The name of an item (items are data, ITEM-DEFINITION): its `items.<id>` translation, or the
 * id itself for an item no catalog names yet (a new item added only in config).
 */
export const itemName = (translator: Pick<Translator, 'lookup'>, id: string): string =>
  translator.lookup(`items.${id}`) ?? id;

/**
 * A structure's name (CORE-ENTITY): definitions are data, so a structure without a translation
 * (one defined only in a config) shows its id instead of failing.
 */
export const structureName = (translator: Pick<Translator, 'lookup'>, id: string): string =>
  translator.lookup(`buildings.${id}`) ?? id;

const ROMAN: readonly [number, string][] = [
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

/** A dynasty's ordinal (NN-1 c), as names bear it: «II», «XIV». */
export function romanOrdinal(n: number): string {
  let left = n;
  let out = '';
  for (const [value, numeral] of ROMAN) {
    while (left >= value) {
      out += numeral;
      left -= value;
    }
  }
  return out;
}

/** A role holder's own name (CREATURE-NAMED): with its ordinal and its byname, no role's words. */
export function holderOwnName(translator: Pick<Translator, 'lookup'>, nm: HolderName): string {
  const ordinal =
    nm.o === undefined
      ? nm.n
      : (translator.lookup('bynames.ordinal', { name: nm.n, ordinal: romanOrdinal(nm.o) }) ?? nm.n);
  // A byname is one text, or one by gender (NN-6: «el Hachas» / «la Hachas»).
  const key = nm.b === undefined ? null : `bynames.${nm.p}.${String(nm.b)}`;
  const byname =
    key === null
      ? null
      : (translator.lookup(`${key}.${nm.f ? 'f' : 'm'}`) ?? translator.lookup(key));
  return byname === null
    ? ordinal
    : (translator.lookup('bynames.joined', { name: ordinal, byname }) ?? ordinal);
}

/**
 * Whoever holds a role, named (CREATURE-NAMED NN-9): its own name in its role's words by its gender
 * («Skrag Uñarrota, el jefe del clan»), each part by its own key.
 */
export function holderName(translator: Pick<Translator, 'lookup'>, nm: HolderName): string {
  const name = holderOwnName(translator, nm);
  return translator.lookup(`roles.${nm.r}.${nm.f ? 'f' : 'm'}`, { name }) ?? name;
}

/** A role while nobody holds it (NN-8): «el jefe del clan». */
export const roleName = (translator: Pick<Translator, 'lookup'>, role: string): string =>
  translator.lookup(`roles.${role}.empty`) ?? role;
